import { useCallback, useMemo, useState } from 'react';

import { useCrossFlow } from '@atlassiansox/cross-flow-support';
import type { TouchpointSourceType } from '@trello/ad-sequencer';
import { useMemberId } from '@trello/authentication';
import { getDateBefore, idToDate } from '@trello/dates';
import type { ExperimentVariations } from '@trello/feature-gate-client';
import { useGetExperimentValue } from '@trello/feature-gate-client';
import {
  useWorkspaceNavigation,
  useWorkspaceNavigationHidden,
} from '@trello/workspace-navigation';

import {
  type TrelloCrossFlowExperiment,
  trelloCrossFlowOpen,
} from '../TrelloCrossFlow';
import { isSourceTargetedByFeatureKey } from './isSourceTargetedByFeatureFlag';
import { useNewUserNudgeFragment } from './NewUserNudgeFragment.generated';
import { useEligibleWorkspacesForProvisioning } from './useEligibleWorkspacesForProvisioning';
const experimentFeatureKey = 'trello_jwm_crossflow_nudge_after_madshot';

export type NewUserNudgesExperimentVariations = ExperimentVariations<
  typeof experimentFeatureKey,
  'cohort'
>;

export interface CrossFlowForNewUserNudgesExperimentParams {
  source: TouchpointSourceType;
  workspaceId?: string;
  boardId?: string;
  hasValidAaSession: boolean;
}

export const useCrossFlowForNewUserNudges = ({
  source,
  workspaceId,
  boardId,
  hasValidAaSession,
}: CrossFlowForNewUserNudgesExperimentParams): TrelloCrossFlowExperiment<NewUserNudgesExperimentVariations> => {
  const crossFlow = useCrossFlow();
  const [fireExposureEvent, setFireExposureEvent] = useState(false);
  const memberId = useMemberId();
  const { data: memberData } = useNewUserNudgeFragment({
    from: { id: memberId },
  });
  const campaigns = memberData?.campaigns ?? [];
  const moonshotCampaign = campaigns?.find(
    (c: { name: string }) => c.name === 'moonshot',
  );
  const wentThroughMadshot =
    moonshotCampaign?.dateDismissed !== undefined &&
    moonshotCampaign.dateDismissed !== null;

  const isNewUser = idToDate(memberId) > getDateBefore({ days: 7 });

  const [{ expanded: _workspaceNavigationExpanded }] = useWorkspaceNavigation();
  const [{ hidden: _workspaceNavigationHidden }] =
    useWorkspaceNavigationHidden();
  const workspaceNavigationExpanded =
    !_workspaceNavigationHidden && _workspaceNavigationExpanded;

  const { cohort } = useGetExperimentValue({
    experimentName: experimentFeatureKey,
    parameter: 'cohort',
    fireExposureEvent,
  });

  const eligibleWorkspaceOptions = useEligibleWorkspacesForProvisioning({
    workspaceId,
  });

  const isRuntimeEligibilityChecksPassed = useMemo(
    () =>
      wentThroughMadshot &&
      isNewUser &&
      cohort !== 'not-enrolled' &&
      isSourceTargetedByFeatureKey({
        featureKey: experimentFeatureKey,
        source,
      }) &&
      workspaceNavigationExpanded,
    [
      cohort,
      isNewUser,
      source,
      wentThroughMadshot,
      workspaceNavigationExpanded,
    ],
  );

  const getIsTargeted = useCallback(() => {
    return cohort !== 'not-enrolled';
  }, [cohort]);

  const getExperimentCohort = useCallback(() => {
    if (isRuntimeEligibilityChecksPassed) {
      setFireExposureEvent(true);
      return cohort;
    }
    return 'not-enrolled';
  }, [cohort, isRuntimeEligibilityChecksPassed]);

  return useMemo(
    () => ({
      isEnrolled: isRuntimeEligibilityChecksPassed,
      experimentName: experimentFeatureKey,
      getIsTargeted,
      getExperimentCohort,
      open: trelloCrossFlowOpen(
        crossFlow,
        source,
        eligibleWorkspaceOptions,
        hasValidAaSession,
        {
          isNewTrelloUser: true,
        },
      ),
    }),
    [
      crossFlow,
      eligibleWorkspaceOptions,
      getExperimentCohort,
      getIsTargeted,
      hasValidAaSession,
      isRuntimeEligibilityChecksPassed,
      source,
    ],
  );
};
